import { useContext, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useFilter } from "@stadium/shared/src/hooks/use-filter";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import CheckboxDropdown from "@sharedComponents/dropdown/checkbox-dropdown";
import { SystemFilterKeys } from "@stadium/redux-utils/constants/filter";

import { useStoreContext } from "@components/store/provider";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import { getIsPricelessShop } from "@selectors/store";
import { makeFilterSort } from "@stadium/redux-utils/selectors/filterSelector";

import {
  RELEVANCE_SORT_ITEM,
  RECOMMENDED_SORT_ITEM,
  SORT_ITEMS,
} from "@constants/products/listing";
import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_SORT } from "@constants/analytics/filter";

import styles from "./listing-header.module.scss";

const ProductSortDropdown = ({ source }) => {
  const { currentSortBy, setSortBy } = useContext(ProductListingContext);
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const {
    isNewSwagmagicHost,
    storeNumber,
  } = useStoreContext();

  const withAnalytics = useWithAnalytics();

  const selectSelectedSortOption = useMemo(
    () => makeFilterSort(source === "search" ? RELEVANCE_SORT_ITEM.id : RECOMMENDED_SORT_ITEM.id),
    [source],
  );
  const currentSelectedSortOption = useSelector(selectSelectedSortOption);

  const additionalItems = useMemo(() => {
    if (source === "search") {
      return [RELEVANCE_SORT_ITEM];
    }
    return [RECOMMENDED_SORT_ITEM];
  }, [source]);

  const activeSortBy =
    isNewSwagmagicHost && currentSelectedSortOption ? currentSelectedSortOption : currentSortBy;

  const { onChange } = useFilter();

  const items = useMemo(() => {
    const filteredItems = isPricelessShop
      ? SORT_ITEMS.filter((item) => !item.priceDependent)
      : SORT_ITEMS;
    return [...additionalItems, ...filteredItems];
  }, [additionalItems, isPricelessShop]);

  const shopAnalyticsContext = useShopAnalyticsContext();

  const handleSortChange = useCallback(
    (id) => {
      if (storeNumber) {
        const sortLabel = items.find((item) => item.id === id)?.label || id;
        withAnalytics(undefined, {
          track: true,
          target: ANALYTICS_TARGET_SORT,
          action: ANALYTICS_EVENT_ACTION_CHANGED,
          eventProperties: {
            ...shopAnalyticsContext,
            sort_type: sortLabel,
          },
        })();
      }

      if (isNewSwagmagicHost) {
        onChange(SystemFilterKeys.SORT, { id: SystemFilterKeys.SORT, value: id });
        return;
      }
      setSortBy(id);
    },
    [
      isNewSwagmagicHost,
      onChange,
      setSortBy,
      storeNumber,
      withAnalytics,
      shopAnalyticsContext,
      items,
    ],
  );

  const data = useMemo(
    () => [{ items, key: "sort", onChange: handleSortChange, selectedIds: [activeSortBy] }],
    [items, handleSortChange, activeSortBy],
  );

  const label = useMemo(() => {
    const item = items.find((item) => item.id === activeSortBy);
    const desktopLabel = isNewSwagmagicHost ? `${item?.label}` : `SORT BY: ${item?.label}`;
    const mobileLabel = "Sort By";
    return isMobileDevice ? mobileLabel : desktopLabel;
  }, [items, activeSortBy, isMobileDevice, isNewSwagmagicHost]);

  const renderSortDropdown = () => (
    <div className={isNewSwagmagicHost ? styles.sortDropdownV2 : ""}>
      {!isMobileDevice && isNewSwagmagicHost && (
        <span className={styles.sortDropdownLabel}>SORT BY</span>
      )}
      <CheckboxDropdown
        id="sort"
        label={label}
        parentLabel="Sort"
        data={data}
        className={isNewSwagmagicHost ? styles.sortCheckboxDropdownV2 : ""}
      />
    </div>
  );

  return isMobileDevice ? (
    <BottomIconHideWrapper>{renderSortDropdown()}</BottomIconHideWrapper>
  ) : (
    renderSortDropdown()
  );
};

export default ProductSortDropdown;
